// pages/LocalizarHuesped.jsx
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Radio,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Card,
  Statistic,
  Modal,
  Descriptions,
  Divider,
  Badge,
  DatePicker,
  InputNumber
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  HomeOutlined,
  IdcardOutlined,
  EyeOutlined,
  FileTextOutlined,
  ExportOutlined,  
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

import { useNavigate, useParams } from 'react-router-dom';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const LocalizarHuesped = () => {

    const navigate = useNavigate();

  // Estados
  const [huespedes, setHuespedes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoteles, setHoteles] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [todosHoteles, setTodosHoteles] = useState(false);
  
  // Filtros de búsqueda
  const [searchNombre, setSearchNombre] = useState('');
  const [searchHabitacion, setSearchHabitacion] = useState('');
  const [searchDocumento, setSearchDocumento] = useState('');
  const [estadoReserva, setEstadoReserva] = useState('ocupantes'); // ocupantes, finalizadas, pendientes
  
  // Modal de detalles
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedHuesped, setSelectedHuesped] = useState(null);
  const [detallesLoading, setDetallesLoading] = useState(false);
  
  // Estadísticas
  const [stats, setStats] = useState({
    total: 0,
    ocupantes: 0,
    finalizadas: 0,
    pendientes: 0
  });
  
  // Configuración de columnas
  const columns = [
    {
      title: 'Hotel',
      dataIndex: 'hotel',
      key: 'hotel',
      width: 150,
      fixed: 'left',
      render: (hotel) => (
        <Space>
          <HomeOutlined style={{ color: '#1890ff' }} />
          <Text strong>{hotel || 'N/A'}</Text>
        </Space>
      )
    },
    {
      title: 'Habitación',
      dataIndex: 'numHab',
      key: 'numHab',
      width: 100,
      sorter: (a, b) => (a.numHab || '').localeCompare(b.numHab || ''),
      render: (numHab) => (
        <Tag color="blue" style={{ fontSize: '14px', fontWeight: 'bold' }}>
          <HomeOutlined style={{ marginRight: 4 }} />
          {numHab || 'Sin asignar'}
        </Tag>
      )
    },
    {
      title: 'Huésped',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 200,
      sorter: (a, b) => (a.nombre || '').localeCompare(b.nombre || ''),
      render: (nombre) => (
        <div>
          <UserOutlined style={{ color: '#52c41a', marginRight: 8 }} />
          <Text strong>{nombre}</Text>
        </div>
      )
    },
    {
      title: 'Reserva',
      dataIndex: 'numReserva',
      key: 'numReserva',
      width: 100,
      render: (numReserva, record) => (
        <Tooltip title={`Línea: ${record.linea}`}>
          <Tag color="purple">
            #{numReserva}-{record.linea}
          </Tag>
        </Tooltip>
      )
    },
    {
      title: 'Entrada',
      dataIndex: 'entrada',
      key: 'entrada',
      width: 110,
      sorter: (a, b) => dayjs(a.entrada).unix() - dayjs(b.entrada).unix(),
      render: (entrada) => (
        <div>
          <CalendarOutlined style={{ marginRight: 4, color: '#fa8c16' }} />
          {entrada ? dayjs(entrada).format('DD/MM/YYYY') : '-'}
        </div>
      )
    },
    {
      title: 'Salida',
      dataIndex: 'salida',
      key: 'salida',
      width: 110,
      sorter: (a, b) => dayjs(a.salida).unix() - dayjs(b.salida).unix(),
      render: (salida) => (
        <div>
          <CalendarOutlined style={{ marginRight: 4, color: '#fa8c16' }} />
          {salida ? dayjs(salida).format('DD/MM/YYYY') : '-'}
        </div>
      )
    },
    {
      title: 'Tipo Hab.',
      dataIndex: 'tipoHab',
      key: 'tipoHab',
      width: 100,
      render: (tipoHab) => <Tag>{tipoHab || '-'}</Tag>
    },
    {
      title: 'Régimen',
      dataIndex: 'regimen',
      key: 'regimen',
      width: 100,
      render: (regimen) => <Tag color="cyan">{regimen || '-'}</Tag>
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 120,
      render: (_, record) => {
        if (record.anulada) {
          return <Badge status="error" text="Anulada" />;
        }
        if (record.entro && !record.salio) {
          return <Badge status="success" text="Ocupando" />;
        }
        if (record.entro && record.salio) {
          return <Badge status="default" text="Finalizada" />;
        }
        return <Badge status="warning" text="Pendiente" />;
      }
    },
    // {
    //   title: 'Acciones',
    //   key: 'actions',
    //   width: 100,
    //   fixed: 'right',
    //   render: (_, record) => (
    //     <Tooltip title="Ver detalles">
    //       <Button
    //         type="primary"
    //         icon={<EyeOutlined />}
    //         size="small"
    //         onClick={() => handleVerDetalles(record)}
    //       />
    //     </Tooltip>
    //   )
    // }
  ];
  
  // Cargar hoteles al iniciar
  useEffect(() => {
    fetchHoteles();
  }, []);
  
  const fetchHoteles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/particulares/hoteles/disponibles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setHoteles(response.data.data);
        if (response.data.data.length > 0 && !selectedHotel) {
          setSelectedHotel(response.data.data[0].codigo);
        }
      }
    } catch (error) {
      console.error('Error cargando hoteles:', error);
      message.error('Error al cargar la lista de hoteles');
    }
  };
  
  const buscarHuespedes = async () => {
    // Validar que al menos haya un criterio de búsqueda
    if (!searchNombre.trim() && !searchHabitacion.trim() && !searchDocumento.trim()) {
      message.warning('Ingrese al menos un criterio de búsqueda (nombre, habitación o documento)');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = {
        estado: estadoReserva,
        todosHoteles: todosHoteles ? 'true' : 'false'
      };
      
      if (!todosHoteles && selectedHotel) {
        params.codigoHotel = selectedHotel;
      }
      
      if (searchNombre.trim()) {
        params.nombre = searchNombre.trim();
      }
      
      if (searchHabitacion.trim()) {
        params.habitacion = searchHabitacion.trim();
      }
      
      if (searchDocumento.trim()) {
        params.documento = searchDocumento.trim();
      }
      
      const response = await axios.get(`${BASE_URL}/api/particulares/buscar/huesped`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      if (response.data.success) {
        const data = response.data.data;
        setHuespedes(data);
        
        // Calcular estadísticas
        const nuevosStats = {
          total: data.length,
          ocupantes: data.filter(h => h.entro && !h.salio && !h.anulada).length,
          finalizadas: data.filter(h => h.entro && h.salio && !h.anulada).length,
          pendientes: data.filter(h => !h.entro && !h.salio && !h.anulada).length
        };
        setStats(nuevosStats);
        
        if (data.length === 0) {
          message.info('No se encontraron huéspedes con los criterios especificados');
        } else {
          message.success(`Se encontraron ${data.length} huéspedes`);
        }
      }
    } catch (error) {
      console.error('Error en búsqueda:', error);
      message.error('Error al buscar huéspedes');
      setHuespedes([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerDetalles = async (record) => {
    setModalVisible(true);
    setDetallesLoading(true);
    setSelectedHuesped(record);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/particulares/reserva/${record.numReserva}/${record.linea}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setSelectedHuesped({ ...record, ...response.data.data });
      }
    } catch (error) {
      console.error('Error cargando detalles:', error);
      message.error('Error al cargar los detalles de la reserva');
    } finally {
      setDetallesLoading(false);
    }
  };
  
  const handleReset = () => {
    setSearchNombre('');
    setSearchHabitacion('');
    setSearchDocumento('');
    setEstadoReserva('ocupantes');
    setHuespedes([]);
    setStats({ total: 0, ocupantes: 0, finalizadas: 0, pendientes: 0 });
    message.info('Filtros restablecidos');
  };
  
  const handleExportar = () => {
    if (huespedes.length === 0) {
      message.warning('No hay datos para exportar');
      return;
    }
    
    // Exportar a CSV
    const headers = ['Hotel', 'Habitación', 'Huésped', 'Reserva', 'Entrada', 'Salida', 'Tipo Hab.', 'Régimen', 'Estado'];
    const data = huespedes.map(h => [
      h.hotel,
      h.numHab,
      h.nombre,
      `${h.numReserva}-${h.linea}`,
      dayjs(h.entrada).format('DD/MM/YYYY'),
      dayjs(h.salida).format('DD/MM/YYYY'),
      h.tipoHab,
      h.regimen,
      h.anulada ? 'Anulada' : (h.entro && !h.salio ? 'Ocupando' : (h.entro && h.salio ? 'Finalizada' : 'Pendiente'))
    ]);
    
    const csvContent = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `huespedes_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    message.success('Exportación completada');
  };
  
  // Barra de búsqueda mejorada
  const searchBar = (
    <Card style={{ marginBottom: 24, borderRadius: 12 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Input
            placeholder="Buscar por nombre del huésped"
            value={searchNombre}
            onChange={(e) => setSearchNombre(e.target.value)}
            onPressEnter={buscarHuespedes}
            prefix={<UserOutlined />}
            size="large"
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Input
            placeholder="Número de habitación"
            value={searchHabitacion}
            onChange={(e) => setSearchHabitacion(e.target.value)}
            onPressEnter={buscarHuespedes}
            prefix={<HomeOutlined />}
            size="large"
            allowClear
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Input
            placeholder="N° Documento (NIF/Pasaporte/etc)"
            value={searchDocumento}
            onChange={(e) => setSearchDocumento(e.target.value)}
            onPressEnter={buscarHuespedes}
            prefix={<IdcardOutlined />}
            size="large"
            allowClear
          />
        </Col>
      </Row>
      
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Radio.Group 
            value={estadoReserva} 
            onChange={(e) => setEstadoReserva(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="ocupantes">
              <CheckCircleOutlined /> Ocupantes
            </Radio.Button>
            <Radio.Button value="finalizadas">
              <CloseCircleOutlined /> Finalizadas
            </Radio.Button>
            <Radio.Button value="pendientes">
              <ClockCircleOutlined /> Pendientes
            </Radio.Button>
          </Radio.Group>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Select
            style={{ width: '100%' }}
            value={selectedHotel}
            onChange={setSelectedHotel}
            disabled={todosHoteles}
            placeholder="Seleccionar hotel"
          >
            {hoteles.map(hotel => (
              <Option key={hotel.codigo} value={hotel.codigo}>
                {hotel.nombre}
              </Option>
            ))}
          </Select>
        </Col>
        
        <Col xs={24} sm={12} md={4}>
          <Button
            type={todosHoteles ? 'primary' : 'default'}
            onClick={() => setTodosHoteles(!todosHoteles)}
            icon={<HomeOutlined />}
            block
          >
            Todos los hoteles
          </Button>
        </Col>
        
        <Col xs={24} sm={24} md={8}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={buscarHuespedes}
              loading={loading}
              size="large"
            >
              Buscar
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              size="large"
            >
              Limpiar
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExportar}
              disabled={huespedes.length === 0}
              size="large"
            >
              Exportar
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );
  
  // Tarjetas de estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Total encontrados"
            value={stats.total}
            prefix={<UserOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Ocupantes actuales"
            value={stats.ocupantes}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Finalizadas"
            value={stats.finalizadas}
            prefix={<CloseCircleOutlined />}
            valueStyle={{ color: '#fa8c16' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Pendientes"
            value={stats.pendientes}
            prefix={<ClockCircleOutlined />}
            valueStyle={{ color: '#ff4d4f' }}
          />
        </Card>
      </Col>
    </Row>
  );
  
  return (
    <>
      <PageContainer
        title="Localizar Huésped"
        subtitle="Búsqueda avanzada de huéspedes por nombre, habitación o documento"
        icon={<UserOutlined />}
        loading={loading}
        onRefresh={buscarHuespedes}
        badgeCount={stats.total}
        badgeStatus={stats.total > 0 ? "processing" : "default"}
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {searchBar}
        {/* {estadisticas} */}
        
        <Card style={{ borderRadius: 12 }}>
          <Table
            columns={columns}
            dataSource={huespedes}
            rowKey={(record) => `${record.numReserva}-${record.linea}`}
            loading={loading}
            scroll={{ x: 1200, y: 500 }}
            size="middle"
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} huéspedes`,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            locale={{
              emptyText: stats.total === 0 && !loading ? 
                'No se encontraron huéspedes. Ingrese criterios de búsqueda.' : 
                'Buscando huéspedes...'
            }}
            onRow={(record) => ({
              onDoubleClick: () => {
                            navigate(`/reservas/${record.numReserva}`)   
                                                                                
                            },
                style: record.anulada ? { backgroundColor: '#fff1f0' } : {}
            })}
          />
        </Card>
      </PageContainer>
      
      {/* Modal de detalles */}
      <Modal
        title={
          <Space>
            <UserOutlined />
            <span>Detalles del Huésped</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Cerrar
          </Button>
        ]}
        width={700}
        loading={detallesLoading}
      >
        {selectedHuesped && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Hotel" span={2}>
                <Tag color="blue" icon={<HomeOutlined />}>
                  {selectedHuesped.hotel}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Reserva">
                <Tag color="purple">
                  #{selectedHuesped.numReserva}-{selectedHuesped.linea}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Habitación">
                <Tag color="blue" icon={<HomeOutlined />}>
                  {selectedHuesped.numHab || 'Sin asignar'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Huésped" span={2}>
                <Text strong>{selectedHuesped.nombre}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Tipo Cliente">
                {selectedHuesped.tipoCliente || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Estado">
                {selectedHuesped.anulada ? (
                  <Badge status="error" text="Anulada" />
                ) : selectedHuesped.entro && !selectedHuesped.salio ? (
                  <Badge status="success" text="Ocupando" />
                ) : selectedHuesped.entro && selectedHuesped.salio ? (
                  <Badge status="default" text="Finalizada" />
                ) : (
                  <Badge status="warning" text="Pendiente" />
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Entrada">
                {selectedHuesped.entrada ? dayjs(selectedHuesped.entrada).format('DD/MM/YYYY') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Salida">
                {selectedHuesped.salida ? dayjs(selectedHuesped.salida).format('DD/MM/YYYY') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo Habitación">
                {selectedHuesped.tipoHab || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Régimen">
                {selectedHuesped.regimen || '-'}
              </Descriptions.Item>
              {selectedHuesped.serEntrada && (
                <Descriptions.Item label="Serie Entrada">
                  {selectedHuesped.serEntrada}
                </Descriptions.Item>
              )}
              {selectedHuesped.serSalida && (
                <Descriptions.Item label="Serie Salida">
                  {selectedHuesped.serSalida}
                </Descriptions.Item>
              )}
            </Descriptions>
            
            {selectedHuesped.TotalFacturado !== undefined && (
              <>
                <Divider />
                <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="Total Facturado"
                        value={selectedHuesped.TotalFacturado || 0}
                        prefix="$"
                        precision={2}
                        valueStyle={{ color: '#3f8600' }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="Estado de Cuenta"
                        value={selectedHuesped.entro && !selectedHuesped.salio ? "Activo" : "Cerrado"}
                        valueStyle={{ color: selectedHuesped.entro && !selectedHuesped.salio ? '#52c41a' : '#8c8c8c' }}
                      />
                    </Col>
                  </Row>
                </Card>
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default LocalizarHuesped;